import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const StatBox = ({ label, value, testId }) => (
  <div className="p-5 rounded-2xl border border-border bg-background/50 text-center">
    <p className="text-xs font-semibold uppercase tracking-wider text-foreground/50">{label}</p>
    <p className="text-2xl md:text-3xl font-bold text-foreground mt-1" data-testid={testId}>{value}</p>
  </div>
);

const SplitList = ({ title, data, labels = {} }) => (
  <div className="p-5 rounded-2xl border border-border bg-background/50">
    <p className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-3">{title}</p>
    {Object.entries(data || {}).map(([k, v]) => (
      <div key={k} className="flex justify-between text-sm py-1 border-b border-border/50 last:border-0">
        <span className="capitalize text-foreground/70">{labels[k] || k}</span>
        <span className="font-semibold text-foreground">{v}</span>
      </div>
    ))}
  </div>
);

const AnalyticsTab = ({ password }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics?password=${encodeURIComponent(password)}`);
      if (res.ok) setData(await res.json());
      else setError('Failed to load analytics');
    } catch {
      setError('Failed to load analytics');
    }
  }, [password]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (error) return <p className="text-red-500">{error}</p>;
  if (!data) return (
    <div className="text-center py-16">
      <TrendingUp className="w-16 h-16 text-muted-foreground mx-auto mb-4 animate-pulse" />
      <p className="text-foreground/60">Loading analytics...</p>
    </div>
  );

  const monthLabel = (m) => new Date(m + '-01T00:00:00').toLocaleDateString('en-AU', { month: 'short', year: '2-digit' });
  const chartData = data.monthly.map(m => ({ ...m, label: monthLabel(m.month) }));

  return (
    <div data-testid="analytics-tab">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Analytics</h1>
        <button onClick={fetchData} className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors" data-testid="refresh-analytics-button">
          <RefreshCw className="w-4 h-4" /><span className="text-sm">Refresh</span>
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatBox label="Total Revenue" value={`$${data.total_revenue.toFixed(2)}`} testId="stat-revenue" />
        <StatBox label="Orders" value={data.total_orders} testId="stat-orders" />
        <StatBox label="Units Sold" value={data.units_sold} testId="stat-units" />
        <StatBox label="Avg Order Value" value={`$${data.avg_order_value.toFixed(2)}`} testId="stat-aov" />
      </div>

      <div className="p-5 rounded-2xl border border-border bg-background/50 mb-6">
        <p className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-4">Revenue by Month</p>
        {chartData.length === 0 ? <p className="text-sm text-foreground/50 italic">No orders yet</p> : (
          <div style={{ width: '100%', height: 260 }} data-testid="revenue-chart">
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `$${v}`} />
                <Tooltip formatter={(v, name) => name === 'revenue' ? [`$${Number(v).toFixed(2)}`, 'Revenue'] : [v, 'Orders']} />
                <Bar dataKey="revenue" fill="#C99A3D" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="p-5 rounded-2xl border border-border bg-background/50">
          <p className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-3">Best Sellers</p>
          {data.best_sellers.length === 0 ? <p className="text-sm text-foreground/50 italic">No sales yet</p> :
            data.best_sellers.map((p, i) => (
              <div key={p.name} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0" data-testid={`best-seller-${i}`}>
                <span className="text-sm font-medium text-foreground">{i + 1}. {p.name}</span>
                <span className="text-sm text-foreground/60">{p.units} units · <strong className="text-foreground">${p.revenue.toFixed(2)}</strong></span>
              </div>
            ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <SplitList title="Payment" data={data.by_payment} labels={{ stripe: 'Card (Stripe)', paypal: 'PayPal' }} />
          <SplitList title="Shipping" data={data.by_shipping} labels={{ postage: 'Delivery', pickup: 'Pickup' }} />
          <SplitList title="Status" data={data.by_status} labels={{ processing: 'Packing', delivered: 'Complete' }} />
        </div>
      </div>
    </div>
  );
};

export default AnalyticsTab;
